import { generateClient } from "aws-amplify/api";
import type { RequestRow, RunRow } from "../utils/historicalModelRuns";
import type { DatasetLinkRow, OutputLinkRow, RunTrigger } from "../utils/historicalRuns";
import type { DatasetUploadRow } from "../utils/datasetUploadApi";
import { getCurrentOwnerClaim } from "../utils/ownerScope";

// Reads and writes for a HISTORICAL model's screens (docs/plans/HISTORICAL_PROCESSED_MODELS_PLAN.md, § Stage 4
// design). Inline GraphQL, like processedModelRunHistoryService: the codegen files were not regenerated for the
// stage 1–3 tables. Every table here is readable by the model's owner, its organization and admins; only the run
// request is written (and only created) by users. A model's data lives in the datasets its outputs write
// (docs/architecture/HISTORICAL_DATA_RUNNERS.md), read through the dataset tabs and query.

const client = generateClient();

const graphqlErrorMessage = (error: any, fallback: string): string => {
  const message = error?.errors?.[0]?.message || error?.message;
  return message ? `${fallback} ${message}` : fallback;
};

/** One GraphQL read; partial data is kept, any other failure throws `fallback` plus AppSync's message. */
export async function query<T>(document: string, variables: Record<string, unknown>, fallback: string): Promise<T> {
  try {
    const response: any = await client.graphql({ query: document, variables });
    return response?.data as T;
  } catch (error) {
    // AppSync returns partial data with errors for fields the caller can't read; keep the data when there is some
    const data = (error as any)?.data;
    if (data && Object.values(data).some((v) => v !== null && v !== undefined)) return data as T;
    throw new Error(graphqlErrorMessage(error, fallback));
  }
}

// ── Selection sets ───────────────────────────────────────────────────────────────────────────────

const REQUEST_FIELDS = `
  id processedModelSchemaId status triggerType triggerRef windowStart windowEnd backfillId chunkIndex chunkCount
  attempts runIds mergedInto notBefore error startedAt completedAt owner organizationId createdAt updatedAt
`;

const RUN_FIELDS = `
  id processedModelSchemaId modelSchemaTemplateId status description startedAt completedAt executionTimeMs
  outputS3Keys inputs requestId triggerType windowStart windowEnd attempt owner organizationId createdAt updatedAt
`;

const OUTPUT_FIELDS = `
  id name unit description channels storageMode targetDatasetId outputLocation active
  parameterTypeId schemaId owner organizationId createdAt updatedAt
`;

const LINK_FIELDS = `
  id processedModelSchemaId datasetId fieldKey columns triggerOnUpload status statusReason crossOrg backfills
`;

// ── Types ────────────────────────────────────────────────────────────────────────────────────────

export interface OutputParamRow {
  id: string;
  name: string;
  unit: string | null;
  description: string | null;
  channels: string[] | null;
  /** FILES (null) = a forecast with dated issues; DATASET = a HISTORICAL output whose runs write targetDatasetId */
  storageMode: "FILES" | "DATASET" | null;
  /** DATASET outputs: the dataset each run writes */
  targetDatasetId: string | null;
  schemaId: string;
}

export interface DatasetFileLite {
  id: string;
  datasetId: string;
  granularity: "DAY" | "MONTH";
  periodStart: string;
  version: number;
  rowCount: number | null;
  minTime: string | null;
  maxTime: string | null;
  active: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────────────────────────────

const asStringList = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null;
  const list = value.filter((entry): entry is string => typeof entry === "string");
  return list.length ? list : null;
};

export const parseJson = <T>(value: unknown, fallback: T): T => {
  if (typeof value !== "string") return (value as T) ?? fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

const toOutput = (item: any): OutputParamRow => ({
  id: item.id,
  name: item.name,
  unit: item.unit ?? null,
  description: item.description ?? null,
  channels: asStringList(item.channels),
  storageMode: item.storageMode === "DATASET" ? "DATASET" : item.storageMode === "FILES" ? "FILES" : null,
  targetDatasetId: item.targetDatasetId ?? null,
  schemaId: item.schemaId,
});

// ── The model, its outputs and links ─────────────────────────────────────────────────────────────

const OUTPUTS_BY_SCHEMA = `
  query OutputsBySchema($schemaId: ID!, $nextToken: String) {
    processedModelOutputParamsBySchemaIdAndName(schemaId: $schemaId, limit: 50, nextToken: $nextToken) {
      items { ${OUTPUT_FIELDS} }
      nextToken
    }
  }
`;

const GET_OUTPUT = `
  query GetOutput($id: ID!) {
    getProcessedModelOutputParam(id: $id) { ${OUTPUT_FIELDS} }
  }
`;

const LINKS_BY_SCHEMA = `
  query LinksBySchema($processedModelSchemaId: ID!) {
    processedModelSchemaDatasetsByProcessedModelSchemaId(processedModelSchemaId: $processedModelSchemaId, limit: 50) {
      items { ${LINK_FIELDS} }
    }
  }
`;

// Model outputs as inputs (docs/architecture/MODEL_OUTPUT_INPUTS.md)
const OUTPUT_LINK_FIELDS = `
  id processedModelSchemaId outputParamId sourceModelId sourceStorageMode fieldKey columns triggerOnUpdate status statusReason crossOrg backfills
`;

const OUTPUT_LINKS_BY_SCHEMA = `
  query OutputLinksBySchema($processedModelSchemaId: ID!) {
    processedModelSchemaOutputsByProcessedModelSchemaId(processedModelSchemaId: $processedModelSchemaId, limit: 50) {
      items { ${OUTPUT_LINK_FIELDS} }
    }
  }
`;

const GET_OUTPUT_LITE = `
  query GetOutputLite($id: ID!) {
    getProcessedModelOutputParam(id: $id) { id name unit storageMode schemaId schema { id name } }
  }
`;

export async function listOutputs(schemaId: string): Promise<OutputParamRow[]> {
  const items: OutputParamRow[] = [];
  let nextToken: string | null = null;
  do {
    const data: any = await query(OUTPUTS_BY_SCHEMA, { schemaId, nextToken }, "Could not load the model's outputs.");
    const page = data?.processedModelOutputParamsBySchemaIdAndName;
    items.push(...(page?.items || []).filter(Boolean).map(toOutput));
    nextToken = page?.nextToken || null;
  } while (nextToken);
  return items;
}

/** The dataset a DATASET output writes, as the model page needs it (docs/architecture/HISTORICAL_DATA_RUNNERS.md). */
export interface TargetDataset {
  id: string;
  name: string;
  timeRange: { startDate?: string | null; endDate?: string | null } | null;
  lastUpdated: string | null;
  curationMode: string | null;
  /** The dataset's S3 folder (its curated/ and incoming/ live under it) */
  routeLong: string | null;
  /** AWSJSON: the declared columns ({columns: [{name, label, unit}]}) */
  ingestMetadata: unknown;
}

const GET_TARGET_DATASET = /* GraphQL */ `
  query GetTargetDataset($id: ID!) {
    getDataset(id: $id) { id name timeRange lastUpdated curationMode routeLong ingestMetadata deletedAt }
  }
`;

export async function getTargetDataset(id: string): Promise<TargetDataset | null> {
  const data: any = await query(GET_TARGET_DATASET, { id }, "Could not load the output's dataset.");
  const row = data?.getDataset;
  if (!row || row.deletedAt) return null;
  let timeRange = row.timeRange;
  try {
    timeRange = typeof timeRange === "string" ? JSON.parse(timeRange) : timeRange;
  } catch {
    timeRange = null;
  }
  return {
    id: row.id,
    name: row.name || row.id,
    timeRange: timeRange ?? null,
    lastUpdated: row.lastUpdated ?? null,
    curationMode: row.curationMode ?? null,
    routeLong: row.routeLong ?? null,
    ingestMetadata: row.ingestMetadata ?? null,
  };
}

export async function getOutput(id: string): Promise<OutputParamRow | null> {
  const data: any = await query(GET_OUTPUT, { id }, "Could not load the output.");
  return data?.getProcessedModelOutputParam ? toOutput(data.getProcessedModelOutputParam) : null;
}

export async function listDatasetLinks(processedModelSchemaId: string): Promise<DatasetLinkRow[]> {
  const data: any = await query(LINKS_BY_SCHEMA, { processedModelSchemaId }, "Could not load the model's dataset links.");
  return ((data?.processedModelSchemaDatasetsByProcessedModelSchemaId?.items || []) as any[]).filter(Boolean).map((item) => ({
    id: item.id,
    processedModelSchemaId: item.processedModelSchemaId,
    fieldKey: item.fieldKey,
    datasetId: item.datasetId,
    columns: asStringList(item.columns) ?? [],
    triggerOnUpload: item.triggerOnUpload !== false,
    status: item.status,
    statusReason: item.statusReason ?? null,
    crossOrg: item.crossOrg ?? null,
    backfills: item.backfills ?? null,
  }));
}

/** The other models' outputs this model reads (any model kind). */
export async function listOutputLinks(processedModelSchemaId: string): Promise<OutputLinkRow[]> {
  const data: any = await query(OUTPUT_LINKS_BY_SCHEMA, { processedModelSchemaId }, "Could not load the model's output links.");
  return ((data?.processedModelSchemaOutputsByProcessedModelSchemaId?.items || []) as any[]).filter(Boolean).map((item) => ({
    id: item.id,
    processedModelSchemaId: item.processedModelSchemaId,
    fieldKey: item.fieldKey,
    outputParamId: item.outputParamId,
    sourceModelId: item.sourceModelId,
    sourceStorageMode: "FILES",
    columns: asStringList(item.columns) ?? [],
    triggerOnUpdate: item.triggerOnUpdate !== false,
    status: item.status,
    statusReason: item.statusReason ?? null,
    crossOrg: item.crossOrg ?? null,
    backfills: item.backfills ?? null,
  }));
}

export interface OutputLite {
  id: string;
  name: string;
  unit: string | null;
  storageMode: "FILES" | "DATASET" | null;
  schemaId: string;
  modelName: string | null;
}

const outputLiteCache = new Map<string, Promise<OutputLite | null>>();

/** An output's name and its model's name, cached per page load; null when the viewer can't read it. */
export function getOutputLite(id: string): Promise<OutputLite | null> {
  if (!outputLiteCache.has(id)) {
    outputLiteCache.set(
      id,
      query<any>(GET_OUTPUT_LITE, { id }, "Could not load the output.")
        .then((data) => {
          const o = data?.getProcessedModelOutputParam;
          return o ? { id: o.id, name: o.name, unit: o.unit ?? null, storageMode: o.storageMode ?? null, schemaId: o.schemaId, modelName: o.schema?.name ?? null } : null;
        })
        .catch(() => null)
    );
  }
  return outputLiteCache.get(id) as Promise<OutputLite | null>;
}

// ── Requests and runs ────────────────────────────────────────────────────────────────────────────

const REQUESTS_BY_SCHEMA = `
  query RequestsBySchema($processedModelSchemaId: ID!, $limit: Int, $nextToken: String) {
    processedModelRunRequestsByProcessedModelSchemaIdAndCreatedAt(
      processedModelSchemaId: $processedModelSchemaId
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items { ${REQUEST_FIELDS} }
      nextToken
    }
  }
`;

const RUNS_BY_SCHEMA = `
  query HistoricalRunsBySchema($processedModelSchemaId: ID!, $limit: Int, $nextToken: String) {
    processedModelRunHistoriesByProcessedModelSchemaIdAndCreatedAt(
      processedModelSchemaId: $processedModelSchemaId
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items { ${RUN_FIELDS} }
      nextToken
    }
  }
`;

/** The run log is grouped client-side (decision 3), so the whole list is read: at most this many rows. */
export const MAX_LOG_ROWS = 2000;

const toRequest = (item: any): RequestRow => ({ ...item, runIds: parseJson<unknown>(item.runIds, null) }) as RequestRow;

/** Every request of the model, newest first (up to MAX_LOG_ROWS). */
export async function listRunRequests(processedModelSchemaId: string): Promise<RequestRow[]> {
  const items: RequestRow[] = [];
  let nextToken: string | null = null;
  do {
    const data: any = await query(REQUESTS_BY_SCHEMA, { processedModelSchemaId, limit: 200, nextToken }, "Could not load the run log.");
    const page = data?.processedModelRunRequestsByProcessedModelSchemaIdAndCreatedAt;
    items.push(...(page?.items || []).filter(Boolean).map(toRequest));
    nextToken = page?.nextToken || null;
  } while (nextToken && items.length < MAX_LOG_ROWS);
  return items;
}

/** Every historical run (attempt) of the model, newest first (up to MAX_LOG_ROWS). */
export async function listRuns(processedModelSchemaId: string): Promise<RunRow[]> {
  const items: RunRow[] = [];
  let nextToken: string | null = null;
  do {
    const data: any = await query(RUNS_BY_SCHEMA, { processedModelSchemaId, limit: 200, nextToken }, "Could not load the runs.");
    const page = data?.processedModelRunHistoriesByProcessedModelSchemaIdAndCreatedAt;
    items.push(...(page?.items || []).filter(Boolean));
    nextToken = page?.nextToken || null;
  } while (nextToken && items.length < MAX_LOG_ROWS);
  return items;
}

const CREATE_REQUEST = `
  mutation CreateRunRequest($input: CreateProcessedModelRunRequestInput!) {
    createProcessedModelRunRequest(input: $input) { ${REQUEST_FIELDS} }
  }
`;

/** Ask for a run (stage 2, decision 3): MANUAL with no range, or BACKFILL over [windowStart, windowEnd). The run
 * function checks the requester may run the model and refuses with a FAILED row otherwise. */
export async function createRunRequest(input: {
  processedModelSchemaId: string;
  triggerType: Extract<RunTrigger, "MANUAL" | "BACKFILL">;
  windowStart?: string;
  windowEnd?: string;
}): Promise<RequestRow> {
  // AppSync stamps owner only for callers it authorised by the owner rule; an Admin (static group) must send it,
  // or the run function finds no requester and refuses the request. The value is the identity claim, which the
  // owner rule accepts for everyone else too.
  const owner = await getCurrentOwnerClaim();
  const data: any = await query(
    CREATE_REQUEST,
    { input: { status: "QUEUED", ...(owner ? { owner } : {}), ...input } },
    "Could not queue the run."
  );
  const created = data?.createProcessedModelRunRequest;
  if (!created) throw new Error("Could not queue the run.");
  return toRequest(created);
}

// ── Live rows ────────────────────────────────────────────────────────────────────────────────────

export const ON_REQUEST_CREATED = `
  subscription OnRequestCreated($filter: ModelSubscriptionProcessedModelRunRequestFilterInput) {
    onCreateProcessedModelRunRequest(filter: $filter) { ${REQUEST_FIELDS} }
  }
`;
export const ON_REQUEST_UPDATED = `
  subscription OnRequestUpdated($filter: ModelSubscriptionProcessedModelRunRequestFilterInput) {
    onUpdateProcessedModelRunRequest(filter: $filter) { ${REQUEST_FIELDS} }
  }
`;
export const ON_RUN_CREATED = `
  subscription OnHistoricalRunCreated($filter: ModelSubscriptionProcessedModelRunHistoryFilterInput) {
    onCreateProcessedModelRunHistory(filter: $filter) { ${RUN_FIELDS} }
  }
`;
export const ON_RUN_UPDATED = `
  subscription OnHistoricalRunUpdated($filter: ModelSubscriptionProcessedModelRunHistoryFilterInput) {
    onUpdateProcessedModelRunHistory(filter: $filter) { ${RUN_FIELDS} }
  }
`;

export interface Unsubscribable {
  unsubscribe: () => void;
}

/** Subscribe to one of the ON_* documents for a model; `onError` is called once if AppSync refuses or drops it. */
export function subscribe<T>(
  document: string,
  processedModelSchemaId: string,
  onData: (data: T) => void,
  onError: (error: Error) => void
): Unsubscribable {
  const observable = client.graphql({
    query: document,
    variables: { filter: { processedModelSchemaId: { eq: processedModelSchemaId } } },
  }) as unknown as { subscribe: (h: { next: (p: { data: T }) => void; error: (e: Error) => void }) => Unsubscribable };
  return observable.subscribe({
    next: ({ data }) => onData(data),
    error: (error) => onError(error),
  });
}

// ── The dataset uploads a run wrote ──────────────────────────────────────────────────────────────

const GET_DATASET_UPLOAD_LITE = `
  query GetDatasetUploadLite($id: ID!) {
    getDatasetUpload(id: $id) {
      id datasetId status fileName rowCount windowStart windowEnd validationErrors warnings failureReason requestedAt receivedAt processedAt
      source runId processedModelSchemaId outputParamId
    }
  }
`;

/** A run's dataset upload, with the window the run's file declared (DatasetUpload.windowStart / windowEnd). */
export type RunUploadRow = DatasetUploadRow & { windowStart?: string | null; windowEnd?: string | null };

/** The upload rows a run issued, one per output (ids `<runId>-1`, `<runId>-2`, …: uploadIdFor), in output order. The
 * run row says how many files it issued (outputS3Keys); a run from before phase 2 wrote a model series store that
 * no longer exists and has none. */
export async function listDatasetUploadsByRun(run: Pick<RunRow, "id" | "outputS3Keys">): Promise<RunUploadRow[]> {
  const keys = parseJson<unknown>(run.outputS3Keys, null);
  const count = Math.max(1, Array.isArray(keys) ? keys.length : 1);
  const rows: RunUploadRow[] = [];
  for (let i = 1; i <= count; i++) {
    const data: any = await query(GET_DATASET_UPLOAD_LITE, { id: `${run.id}-${i}` }, "Could not load the run's outputs.");
    const row = data?.getDatasetUpload;
    if (!row) break;
    rows.push(row as RunUploadRow);
  }
  return rows;
}

// ── Datasets a run read (decision 4) ─────────────────────────────────────────────────────────────

const GET_DATASET_NAME = `
  query GetDatasetName($id: ID!) {
    getDataset(id: $id) { id name curationMode datasetType organizationId owner }
  }
`;

const GET_DATASET_FILE = `
  query GetDatasetFileLite($id: ID!) {
    getDatasetFile(id: $id) { id datasetId granularity periodStart version rowCount minTime maxTime active }
  }
`;

export interface DatasetLite {
  id: string;
  name: string;
  curationMode: string | null;
  datasetType: string | null;
  organizationId: string | null;
  owner: string | null;
}

const datasetCache = new Map<string, Promise<DatasetLite | null>>();

/** The dataset's name and curation, cached per page load; null when the viewer can't read it. */
export function getDatasetLite(id: string): Promise<DatasetLite | null> {
  if (!datasetCache.has(id)) {
    datasetCache.set(
      id,
      query<any>(GET_DATASET_NAME, { id }, "Could not load the dataset.")
        .then((data) => (data?.getDataset ? (data.getDataset as DatasetLite) : null))
        .catch(() => null)
    );
  }
  return datasetCache.get(id) as Promise<DatasetLite | null>;
}

/** The dataset file rows a run read, by id (a few at a time); missing or unreadable ones are left out. */
export async function getDatasetFiles(ids: string[]): Promise<Map<string, DatasetFileLite>> {
  const found = new Map<string, DatasetFileLite>();
  const batch = 8;
  for (let i = 0; i < ids.length; i += batch) {
    const rows = await Promise.all(
      ids.slice(i, i + batch).map((id) =>
        query<any>(GET_DATASET_FILE, { id }, "Could not load the dataset file.")
          .then((data) => (data?.getDatasetFile ? (data.getDatasetFile as DatasetFileLite) : null))
          .catch(() => null)
      )
    );
    rows.forEach((row) => row && found.set(row.id, row));
  }
  return found;
}

// ── Names ────────────────────────────────────────────────────────────────────────────────────────

const GET_USER_NAME = `
  query GetUserName($id: ID!) {
    getUser(id: $id) { id name }
  }
`;

const userCache = new Map<string, Promise<string | null>>();

/** A requester's display name from the request's owner (sub, or sub::username), cached; null when unknown. */
export function getRequesterName(owner: string | null | undefined): Promise<string | null> {
  if (!owner) return Promise.resolve(null);
  const id = owner.includes("::") ? owner.split("::")[0] : owner;
  if (!userCache.has(id)) {
    userCache.set(
      id,
      query<any>(GET_USER_NAME, { id }, "Could not load the user.")
        .then((data) => data?.getUser?.name ?? null)
        .catch(() => null)
    );
  }
  return userCache.get(id) as Promise<string | null>;
}

const GET_TEMPLATE = `
  query GetHistoricalTemplate($id: ID!) {
    getModelSchemaTemplate(id: $id) { id name version executionUrl modelKind historicalConfig fieldDefinitions }
  }
`;

export interface TemplateLite {
  id: string;
  name: string;
  version: string | null;
  executionUrl: string | null;
  modelKind: string | null;
  historicalConfig: Record<string, unknown> | null;
  fieldDefinitions: Record<string, any>;
}

export async function getTemplate(id: string): Promise<TemplateLite | null> {
  const data: any = await query(GET_TEMPLATE, { id }, "Could not load the template.");
  const row = data?.getModelSchemaTemplate;
  if (!row) return null;
  return {
    id: row.id,
    name: row.name,
    version: row.version ?? null,
    executionUrl: row.executionUrl ?? null,
    modelKind: row.modelKind ?? null,
    historicalConfig: parseJson<Record<string, unknown> | null>(row.historicalConfig, null),
    fieldDefinitions: parseJson<Record<string, any>>(row.fieldDefinitions, {}),
  };
}
